import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Home,
  Mail,
  MapPin,
  LanguagesIcon,
  Phone,
  UserRound,
  XCircle,
  Camera,
  Flame,
  Zap,
  Wrench,
  ShieldCheck,
  X,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import "./OssRequestDetailPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const MEASURE_GROUPS = [
  {
    key: "envelope",
    icon: ShieldCheck,
    items: [
      "improve_insulation",
      "replace_old_windows",
      "replace_external_doors",
      "fix_humidity_condensation",
      "improve_airtightness",
      "facade_insulation",
      "roof_waterproofing_insulation",
      "floor_insulation",
      "shading_solar_protection",
    ],
  },
  {
    key: "heating_cooling",
    icon: Flame,
    items: [
      "improve_heating",
      "improve_cooling",
      "replace_old_boiler",
      "upgrade_heating_controls",
      "install_underfloor_heating",
      "upgrade_radiators_emitters",
      "upgrade_ventilation_air_quality",
      "install_mechanical_ventilation",
      "install_heat_pump",
      "dhw_system_upgrade",
    ],
  },
  {
    key: "electricity_renewables",
    icon: Zap,
    items: [
      "install_solar_panels",
      "install_battery_storage",
      "upgrade_electrical_panel",
      "replace_lighting_led",
      "smart_home_automation",
      "install_ev_charger",
      "install_solar_water_heating",
      "metering_monitoring_upgrades",
    ],
  },
  {
    key: "general_renovations",
    icon: Wrench,
    items: [
      "improve_lighting",
      "kitchen_renovation",
      "bathroom_renovation",
      "interior_refurbishment",
      "accessibility_improvements",
      "structural_repairs",
      "flooring_replacement",
      "painting_finishing_works",
      "facade_repairs",
    ],
  },
];

function formatLabel(value = "") {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

function getPhotoUrl(photo) {
  if (typeof photo === "string") return photo;
  return photo?.file_url || photo?.url || photo?.image_url || "";
}

function InfoItem({ label, value, wide = false }) {
  return (
    <div className={`oss-detail__info-item ${wide ? "is-wide" : ""}`}>
      <span>{label}</span>
      <strong>{formatValue(value)}</strong>
    </div>
  );
}

function RejectRequestModal({
  requestToReject,
  rejectingId,
  onCancel,
  onConfirm,
  t,
}) {
  if (!requestToReject) return null;

  return createPortal(
    <div className="oss-reject-modal-backdrop" onClick={onCancel}>
      <div className="oss-reject-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="oss-reject-modal__close"
          onClick={onCancel}
          aria-label={t("buttons.close")}
        >
          <X size={18} />
        </button>
        <h3>{t("rejectModal.title")}</h3>

        <p>
          {t("rejectModal.description", {
            name:
              requestToReject.homeowner?.full_name ||
              requestToReject.homeowner?.email ||
              t("common.homebuyer"),
          })}
        </p>

        <div className="oss-reject-modal__actions">
          <button
            type="button"
            className="oss-reject-modal__secondary"
            onClick={onCancel}
            disabled={Boolean(rejectingId)}
          >
            {t("buttons.cancel")}
          </button>

          <button
            type="button"
            className="oss-reject-modal__danger"
            onClick={onConfirm}
            disabled={Boolean(rejectingId)}
          >
            {rejectingId ? t("buttons.rejecting") : t("buttons.confirmReject")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AcceptRequestModal({
  requestToAccept,
  acceptingId,
  onCancel,
  onConfirm,
  t,
}) {
  if (!requestToAccept) return null;

  return createPortal(
    <div className="oss-reject-modal-backdrop" onClick={onCancel}>
      <div className="oss-reject-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="oss-reject-modal__close"
          onClick={onCancel}
          aria-label={t("buttons.close")}
        >
          <X size={18} />
        </button>
        <h3>{t("acceptModal.title")}</h3>

        <p>
          {t("acceptModal.description", {
            name:
              requestToAccept.homeowner?.full_name ||
              requestToAccept.homeowner?.email ||
              t("common.homebuyer"),
          })}
        </p>

        <div className="oss-reject-modal__actions">
          <button
            type="button"
            className="oss-reject-modal__secondary"
            onClick={onCancel}
            disabled={Boolean(acceptingId)}
          >
            {t("buttons.cancel")}
          </button>

          <button
            type="button"
            className="oss-detail__accept"
            onClick={onConfirm}
            disabled={Boolean(acceptingId)}
          >
            {acceptingId ? t("buttons.accepting") : t("buttons.confirmAccept")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Toast({ message, onClose, t }) {
  if (!message) return null;

  return createPortal(
    <div className="oss-detail-toast">
      <div>
        <strong>{t("toast.errorTitle")}</strong>
        <p>{message}</p>
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={t("toast.closeError")}
      >
        <X size={16} />
      </button>
    </div>,
    document.body,
  );
}

function scrollToTopSmooth() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
}

export default function OssRequestDetailPage() {
  const { t } = useTranslation("oss-request-details1");
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showToast } = useToast();
  const request = state?.request;
  const property = request?.property || {};
  const homeowner = request?.homeowner || {};
  const measures = request?.selected_measures || [];
  const photos = property?.photos || [];

  const validPhotos = React.useMemo(
    () =>
      photos
        .map((photo, index) => ({
          url: getPhotoUrl(photo),
          index,
        }))
        .filter((photo) => photo.url),
    [photos],
  );

  const [activePhotoIndex, setActivePhotoIndex] = React.useState(null);
  const [zoom, setZoom] = React.useState(1);
  const touchStartX = React.useRef(null);

  const hasActivePhoto = activePhotoIndex !== null;
  const activePhoto = hasActivePhoto ? validPhotos[activePhotoIndex] : null;

  const { getAccessTokenSilently } = useAuth0();

  const [currentRequest, setCurrentRequest] = React.useState(request);
  const [error, setError] = React.useState("");

  const [acceptingId, setAcceptingId] = React.useState(null);
  const [requestToAccept, setRequestToAccept] = React.useState(null);

  const [rejectingId, setRejectingId] = React.useState(null);
  const [requestToReject, setRequestToReject] = React.useState(null);

  function openPhoto(index) {
    setActivePhotoIndex(index);
    setZoom(1);
  }

  function closePhoto() {
    setActivePhotoIndex(null);
    setZoom(1);
  }

  function showNextPhoto() {
    if (!validPhotos.length) return;
    setActivePhotoIndex((current) => {
      const next = current === null ? 0 : (current + 1) % validPhotos.length;
      return next;
    });
    setZoom(1);
  }

  function showPreviousPhoto() {
    if (!validPhotos.length) return;
    setActivePhotoIndex((current) => {
      const previous =
        current === null
          ? 0
          : (current - 1 + validPhotos.length) % validPhotos.length;
      return previous;
    });
    setZoom(1);
  }

  function handleWheelZoom(event) {
    event.preventDefault();

    setZoom((current) => {
      const delta = event.deltaY < 0 ? 0.15 : -0.15;
      return Math.min(3, Math.max(1, Number((current + delta).toFixed(2))));
    });
  }

  function handleTouchStart(event) {
    touchStartX.current = event.touches?.[0]?.clientX ?? null;
  }

  function handleTouchEnd(event) {
    if (touchStartX.current === null) return;

    const endX = event.changedTouches?.[0]?.clientX ?? null;
    if (endX === null) return;

    const diff = touchStartX.current - endX;

    if (Math.abs(diff) > 55) {
      if (diff > 0) showNextPhoto();
      else showPreviousPhoto();
    }

    touchStartX.current = null;
  }

  React.useEffect(() => {
    if (!hasActivePhoto) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") closePhoto();
      if (event.key === "ArrowRight") showNextPhoto();
      if (event.key === "ArrowLeft") showPreviousPhoto();
    }

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasActivePhoto, validPhotos.length]);

  async function updateRequestStatus(action, targetRequest) {
    if (!targetRequest) return;

    const isAccept = action === "accept";

    try {
      if (isAccept) {
        setAcceptingId(targetRequest.id);
      } else {
        setRejectingId(targetRequest.id);
      }

      const accessToken = await getAccessTokenSilently();

      const res = await fetch(
        `${API_BASE}/api/oss/connection-requests/${targetRequest.id}/${action}/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${accessToken}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify(
            isAccept
              ? {}
              : {
                  rejection_reason: "",
                },
          ),
        },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.message ||
            data?.error ||
            (isAccept
              ? t("errors.acceptFailed", "The request could not be accepted.")
              : t("errors.rejectFailed", "The request could not be declined.")),
        );
      }

      setCurrentRequest((prev) => ({
        ...prev,

        status: isAccept ? "ACCEPTED" : "DECLINED",
      }));

      setRequestToAccept(null);
      setRequestToReject(null);

      /* =====================================================
       SUCCESS TOAST
       ===================================================== */

      showToast({
        type: "success",

        title: isAccept
          ? t("toast.acceptSuccessTitle", "Request accepted")
          : t("toast.rejectSuccessTitle", "Request declined"),

        message:
          data?.detail ||
          (isAccept
            ? t(
                "toast.acceptSuccessMessage",
                "The homebuyer request was accepted successfully.",
              )
            : t(
                "toast.rejectSuccessMessage",
                "The homebuyer request was declined successfully.",
              )),
      });

      navigate(isAccept ? "/oss/projects" : "/oss/requests", {
        replace: true,

        state: {
          updatedRequestId: targetRequest.id,

          updatedStatus: isAccept ? "ACCEPTED" : "DECLINED",

          project: data?.project || null,
        },
      });
    } catch (err) {
      console.error(`Could not ${action} OSS request:`, err);

      /* =====================================================
       ERROR TOAST
       ===================================================== */

      showToast({
        type: "error",

        title: isAccept
          ? t("toast.acceptErrorTitle", "Request not accepted")
          : t("toast.rejectErrorTitle", "Request not declined"),

        message:
          err?.message ||
          (isAccept
            ? t("errors.acceptFailed", "The request could not be accepted.")
            : t("errors.rejectFailed", "The request could not be declined.")),
      });
    } finally {
      setAcceptingId(null);
      setRejectingId(null);
    }
  }

  if (!request) {
    return (
      <div className="oss-detail">
        <div className="oss-detail__empty">
          <h2>{t("detail.notAvailable")}</h2>
          <p>{t("detail.notAvailableDescription")}</p>
          <button type="button" onClick={() => navigate("/oss/requests")}>
            <ArrowLeft size={14} />
            {t("buttons.back")}
          </button>
        </div>
      </div>
    );
  }

  const address =
    property.full_address ||
    [
      [property.street_name, property.house_number].filter(Boolean).join(" "),
      property.postal_code,
      property.city,
      property.country,
    ]
      .filter(Boolean)
      .join(", ");

  const groupedMeasures = MEASURE_GROUPS.map((group) => ({
    ...group,
    values: group.items.filter((measure) => measures.includes(measure)),
  })).filter((group) => group.values.length > 0);

  const mapQuery =
    property.latitude && property.longitude
      ? `${property.latitude},${property.longitude}`
      : address;

  const isPendingRequest = currentRequest?.status === "PENDING";

  return (
    <div className="oss-detail">
      <button
        type="button"
        className="oss-detail__back"
        onClick={() => navigate("/oss/requests")}
      >
        <ArrowLeft size={14} />
        {t("buttons.backToRequests")}
      </button>

      <div className="oss-detail__header">
        <div>
          <h1>{t("detail.title")}</h1>
          <p>{t("detail.subtitle")}</p>
        </div>

        <span
          className={`oss-detail__status is-${String(
            currentRequest?.status || "PENDING",
          ).toLowerCase()}`}
        >
          {t(
            `statuses.${currentRequest?.status || "PENDING"}`,
            formatLabel(currentRequest?.status || "PENDING"),
          )}
        </span>
      </div>

      <section className="oss-detail__property-card">
        <div className="oss-detail__section-title">
          <Home size={17} />
          <div>
            <h2>{t("detail.propertyDetails")}</h2>
            <p>{t("detail.propertyDetailsSubtitle")}</p>
          </div>
        </div>

        <div className="oss-detail__property-layout">
          <div className="oss-detail__property-info">
            <div className="oss-detail__info-grid">
              <InfoItem wide label={t("property.address")} value={address} />
              <InfoItem
                label={t("property.propertyType")}
                value={formatLabel(property.property_type)}
              />
              <InfoItem
                label={t("property.buildingCondition")}
                value={formatLabel(property.building_condition)}
              />
              <InfoItem
                label={t("property.floorArea")}
                value={
                  property.surface_sqm
                    ? `${property.surface_sqm} ${t("units.squareMeters")}`
                    : ""
                }
              />
              <InfoItem
                label={t("property.rooms")}
                value={property.number_of_rooms}
              />
              <InfoItem
                label={t("property.yearBuilt")}
                value={property.year_of_build}
              />
              <InfoItem
                label={t("property.currentEpc")}
                value={property.epc_rating_current}
              />
              <InfoItem
                label={t("property.targetEpc")}
                value={property.epc_rating_target}
              />
              <InfoItem
                label={t("property.currentConsumption")}
                value={
                  property.kwh_sqm_year_current
                    ? `${property.kwh_sqm_year_current} ${t("units.kwhPerSqm")}`
                    : ""
                }
              />
              <InfoItem
                label={t("property.targetConsumption")}
                value={
                  property.kwh_sqm_year_target
                    ? `${property.kwh_sqm_year_target} ${t("units.kwhPerSqm")}`
                    : ""
                }
              />
              <InfoItem
                label={t("property.heating")}
                value={formatLabel(property.heating_type)}
              />
              <InfoItem
                label={t("property.cooling")}
                value={formatLabel(property.cooling_type)}
              />
              <InfoItem
                label={t("property.country")}
                value={property.country}
              />
              <InfoItem
                label={t("property.region")}
                value={formatLabel(property.region)}
              />
            </div>
          </div>

          <div className="oss-detail__map-card">
            <div className="oss-detail__map-header">
              <MapPin size={15} />
              <span>{t("detail.mapTitle")}</span>
            </div>

            {mapQuery ? (
              <iframe
                title={t("detail.mapTitle")}
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  mapQuery,
                )}&z=18&output=embed`}
                loading="lazy"
                allowFullScreen
              />
            ) : (
              <div className="oss-detail__map-empty">
                {t("detail.mapUnavailable")}
              </div>
            )}
          </div>
        </div>
      </section>

      {request.renovation_loan_amount ? (
        <section className="oss-detail__finance-highlight">
          <div className="oss-detail__finance-content">
            <div className="oss-detail__finance-text">
              <span className="oss-detail__finance-label">
                {t("property.renovationLoanAmount")}
              </span>

              <small className="oss-detail__finance-note">
                {t("property.renovationLoanHint")}
              </small>
            </div>

            <strong className="oss-detail__finance-value">
              €{" "}
              {Number(request.renovation_loan_amount).toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })}
            </strong>
          </div>
        </section>
      ) : null}

      <div className="oss-detail__middle-grid">
        <section className="oss-detail__card">
          <div className="oss-detail__section-title">
            <CheckCircle2 size={17} />
            <div>
              <h2>{t("detail.renovationNeeds")}</h2>
              <p>{t("detail.renovationNeedsSubtitle")}</p>
            </div>
          </div>

          {groupedMeasures.length ? (
            <div className="oss-detail__measure-groups">
              {groupedMeasures.map((group) => {
                const Icon = group.icon;

                return (
                  <div key={group.key} className="oss-detail__measure-group">
                    <div className="oss-detail__measure-group-header">
                      <Icon size={15} />
                      <span>{t(`measureGroups.${group.key}.title`)}</span>
                    </div>

                    <div className="oss-detail__measure-list">
                      {group.values.map((measure) => (
                        <span key={measure}>
                          <CheckCircle2 size={13} />
                          {t(`measures.${measure}.label`, formatLabel(measure))}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="oss-detail__muted">{t("detail.noMeasures")}</p>
          )}
        </section>

        <section className="oss-detail__card">
          <div className="oss-detail__section-title">
            <UserRound size={17} />
            <div>
              <h2>{t("detail.homeownerInfo")}</h2>
              <p>{t("detail.homeownerInfoSubtitle")}</p>
            </div>
          </div>

          <div className="oss-detail__homeowner">
            <div className="oss-detail__homeowner-head">
              <div className="oss-detail__homeowner-avatar">
                {homeowner.profile_image_url ? (
                  <img
                    src={homeowner.profile_image_url}
                    alt={homeowner.full_name || ""}
                  />
                ) : (
                  <span>
                    {(homeowner.first_name?.[0] || "") +
                      (homeowner.last_name?.[0] || "") || "HB"}
                  </span>
                )}
              </div>

              <div>
                <strong>{homeowner.full_name || homeowner.email || "—"}</strong>
              </div>
            </div>

            <p>
              <Mail size={16} />
              <span>{homeowner.email || "—"}</span>
            </p>

            <p>
              <Phone size={16} />
              <span>{homeowner.phone || "—"}</span>
            </p>

            <p>
              <LanguagesIcon size={16} />
              <span>{homeowner.preferred_language || "—"}</span>
            </p>

            {request.message_to_oss ? (
              <div className="oss-detail__homeowner-message">
                <span>{t("detail.homeownerMessage")}</span>
                <p>{request.message_to_oss}</p>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      {validPhotos.length > 0 ? (
        <section className="oss-detail__photos-card">
          <div className="oss-detail__section-title">
            <Camera size={17} />
            <div>
              <h2>{t("detail.propertyPhotos")}</h2>
              <p>{t("detail.propertyPhotosSubtitle")}</p>
            </div>
          </div>

          <div className="oss-detail__photo-preview">
            {validPhotos.slice(0, 1).map((photo, index) => (
              <button
                type="button"
                className="oss-detail__photo-thumb"
                key={`${photo.url}-${index}`}
                onClick={() => openPhoto(index)}
              >
                <img
                  src={photo.url}
                  alt={t("detail.propertyPhotoAlt", { number: index + 1 })}
                  loading="lazy"
                />
              </button>
            ))}

            <button
              type="button"
              className="oss-detail__open-gallery"
              onClick={() => openPhoto(0)}
            >
              <Camera size={16} />
              <strong>
                {t("seeMorePhotos", {
                  count: validPhotos.length,
                  defaultValue: "See {{count}} photos",
                })}
              </strong>
            </button>
          </div>
        </section>
      ) : null}

      <div className="oss-detail__actions">
        <button
          type="button"
          className="oss-detail__accept"
          disabled={
            !isPendingRequest || Boolean(acceptingId) || Boolean(rejectingId)
          }
          onClick={() => {
            if (!isPendingRequest) return;
            scrollToTopSmooth();
            setRequestToAccept(currentRequest);
          }}
        >
          <CheckCircle2 size={15} />
          {acceptingId ? t("buttons.accepting") : t("buttons.accept")}
        </button>

        <button
          type="button"
          className="oss-detail__reject"
          disabled={
            !isPendingRequest || Boolean(acceptingId) || Boolean(rejectingId)
          }
          onClick={() => {
            if (!isPendingRequest) return;
            scrollToTopSmooth();
            setRequestToReject(currentRequest);
          }}
        >
          <XCircle size={15} />
          {rejectingId ? t("buttons.rejecting") : t("buttons.reject")}
        </button>
        <button
          type="button"
          className="oss-detail__contact"
          onClick={() =>
            navigate("/oss/communication", {
              state: { requestId: currentRequest?.id },
            })
          }
        >
          <Mail size={15} />
          {t("buttons.contact")}
        </button>
      </div>

      <Toast message={error} onClose={() => setError("")} t={t} />

      <AcceptRequestModal
        requestToAccept={requestToAccept}
        acceptingId={acceptingId}
        onCancel={() => setRequestToAccept(null)}
        onConfirm={() => updateRequestStatus("accept", requestToAccept)}
        t={t}
      />

      <RejectRequestModal
        requestToReject={requestToReject}
        rejectingId={rejectingId}
        onCancel={() => setRequestToReject(null)}
        onConfirm={() => updateRequestStatus("reject", requestToReject)}
        t={t}
      />

      {activePhoto ? (
        <div
          className="oss-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={closePhoto}
          onWheel={handleWheelZoom}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            className="oss-lightbox__close"
            onClick={closePhoto}
            aria-label={t("buttons.close", "Close")}
          >
            <X size={22} />
          </button>

          <button
            type="button"
            className="oss-lightbox__nav oss-lightbox__nav--left"
            onClick={(e) => {
              e.stopPropagation();
              showPreviousPhoto();
            }}
            aria-label={t("buttons.previous", "Previous")}
          >
            <ChevronLeft size={34} />
          </button>

          <div
            className="oss-lightbox__stage"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              className="oss-lightbox__image"
              src={activePhoto.url}
              alt={t("detail.propertyPhotoAlt", {
                number: activePhotoIndex + 1,
              })}
              style={{ transform: `scale(${zoom})` }}
              draggable={false}
            />

            <div className="oss-lightbox__footer">
              <span>
                {activePhotoIndex + 1} / {validPhotos.length}
              </span>

              <button
                type="button"
                onClick={() => setZoom(1)}
                disabled={zoom === 1}
              >
                <RotateCcw size={14} />
                {t("buttons.resetZoom", "Reset zoom")}
              </button>

              <span>{Math.round(zoom * 100)}%</span>
            </div>
          </div>

          <button
            type="button"
            className="oss-lightbox__nav oss-lightbox__nav--right"
            onClick={(e) => {
              e.stopPropagation();
              showNextPhoto();
            }}
            aria-label={t("buttons.next", "Next")}
          >
            <ChevronRight size={34} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
